import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, ShieldAlert, Activity, CheckCircle2 } from 'lucide-react';
interface ViralResultCardProps {
  score: number;
  scanId?: string;
}
export function ViralResultCard({
  score,
  scanId = 'CHUK-992-X'
}: ViralResultCardProps) {
  // Determine level based on score
  const getLevel = (score: number) => {
    if (score >= 81) return 4;
    if (score >= 51) return 3;
    if (score >= 21) return 2;
    return 1;
  };
  const level = getLevel(score);
  // Configuration for each level
  const config = {
    1: {
      title: 'The Saint',
      color: '#00FF00',
      glowColor: 'rgba(0, 255, 0, 0.5)',
      quote: 'Forensic Analysis indicates a high degree of emotional transparency. No manipulation tactics, DARVO, or gaslighting signatures were detected. This conversation reflects a secure attachment pattern and healthy conflict resolution. Reality Status: Grounded.',
      status: '✅ CLEAR',
      gaslighting: 0,
      loveBombing: 1,
      darvo: 0,
      subHeader: 'Healthy Pattern Detected',
      icon: <CheckCircle2 size={16} data-id="element-507" />
    },
    2: {
      title: 'Proceed with Caution',
      color: '#FFBF00',
      glowColor: 'rgba(255, 191, 0, 0.5)',
      quote: "Analysis detected minor 'Reactive Friction.' While systematic manipulation is absent, there are traces of defensive communication and 'Kitchen Sinking' (bringing up past issues). These are yellow flags—monitor for patterns of escalation. Reality Status: Stable, but Watchful.",
      status: '⚠️ WATCHFUL',
      gaslighting: 2,
      loveBombing: 2,
      darvo: 1,
      subHeader: 'Reactive Friction Detected',
      icon: <Activity size={16} data-id="element-508" />
    },
    3: {
      title: 'The Drama Specialist',
      color: '#FF6600',
      glowColor: 'rgba(255, 102, 0, 0.5)',
      quote: "Warning: High-intensity emotional volatility detected. The AI identified recurring 'Intermittent Reinforcement'—a cycle of hot and cold behavior designed to keep you anxious. Your nervous system is likely in a state of 'Hyper-Vigilance.' Reality Status: Under Pressure.",
      status: '⚠️ HIGH ALERT',
      gaslighting: 3,
      loveBombing: 4,
      darvo: 3,
      subHeader: 'Intermittent Reinforcement Detected',
      icon: <AlertTriangle size={16} data-id="element-509" />
    },
    4: {
      title: 'The Final Boss of Gaslighting',
      color: '#FF0033',
      glowColor: 'rgba(255, 0, 51, 0.5)',
      quote: "CRITICAL ALERT: Forensic signature confirms a systematic 'Silent Drain' protocol. The subject is utilizing DARVO and Reality Distortion to erode your self-trust. This is not a 'bad mood'; it is a tactical attempt to rewrite your memories and gain total control. Reality Status: Compromised. Trust the Data.",
      status: '⚠️ CRITICAL RED FLAG',
      gaslighting: 5,
      loveBombing: 5,
      darvo: 4,
      subHeader: '"The Silent Drain" Pattern Detected',
      icon: <ShieldAlert size={16} data-id="element-510" />
    }
  };
  const currentConfig = config[level as keyof typeof config];
  // SVG Circle Math
  const radius = 120;
  const strokeWidth = 16;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - score / 100 * circumference;
  const ringSize = 280;
  // Helper for indicator dots
  const renderDots = (activeCount: number, label: string, severity: string) => {
    return <div className="flex items-center justify-between mb-3" data-id="element-511">
        <span className="text-[#F0F0F0] font-mono text-sm" data-id="element-512">{label}:</span>
        <div className="flex items-center gap-3" data-id="element-513">
          <div className="flex gap-1.5" data-id="element-514">
            {[1, 2, 3, 4, 5].map(dot => <div key={dot} className="w-2.5 h-2.5 rounded-full" style={{
            backgroundColor: dot <= activeCount ? 'var(--theme-color)' : '#1f2937',
            boxShadow: dot <= activeCount ? '0 0 8px var(--theme-color)' : 'none'
          }} data-id="element-515" />)}
          </div>
          <span className="text-gray-500 font-mono text-xs w-16 text-right uppercase tracking-wider" data-id="element-516">
            ({severity})
          </span>
        </div>
      </div>;
  };
  return <div className="w-full max-w-[400px] aspect-[9/16] bg-[#0A0A0A] relative overflow-hidden flex flex-col font-sans border border-gray-800 shadow-2xl mx-auto rounded-3xl" style={{
    WebkitFontSmoothing: 'antialiased',
    '--theme-color': currentConfig.color,
    '--theme-color-40': `${currentConfig.color}40`,
    '--theme-color-10': `${currentConfig.color}10`
  } as React.CSSProperties} data-id="element-517">
      {/* Background Grid & Noise */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" data-id="element-518" />
      <div className="absolute inset-0 opacity-20 mix-blend-overlay pointer-events-none bg-[url('data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noiseFilter%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.65%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noiseFilter)%22/%3E%3C/svg%3E')]" data-id="element-519" />

      {/* Header */}
      <header className="p-5 border-b border-gray-800 relative z-10 bg-[#0A0A0A]/80 backdrop-blur-md" data-id="element-520">
        <div className="flex justify-between items-start mb-3" data-id="element-521">
          <div className="font-mono text-[10px] text-gray-500 tracking-widest uppercase" data-id="element-522">
            Scan ID: #{scanId}
          </div>
          <div className="flex items-center gap-1.5 px-2 py-1 rounded border font-mono text-[9px] font-bold tracking-wider" style={{
          color: 'var(--theme-color)',
          borderColor: 'var(--theme-color-40)',
          backgroundColor: 'var(--theme-color-10)'
        }} data-id="element-523">
            {currentConfig.icon}
            {currentConfig.status}
          </div>
        </div>
        <h1 className="font-mono text-[#F0F0F0] text-sm font-bold tracking-widest" data-id="element-524">
          TOXIC DETECTOR™ | FORENSIC ANALYSIS
        </h1>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 relative z-10" data-id="element-525">
        {/* Meme-able Label */}
        <div className="mb-6 text-center" data-id="element-526">
          <h2 className="text-2xl font-black text-[#F0F0F0] uppercase tracking-tight" data-id="element-527">
            {currentConfig.title}
          </h2>
        </div>

        {/* Glowing Gauge */}
        <div className="relative flex items-center justify-center mb-6" style={{
        width: ringSize,
        height: ringSize
      }} data-id="element-528">
          {/* Neon Glow Box-Shadow effect behind the circle */}
          <div className="absolute inset-0 rounded-full blur-[40px] opacity-40 pointer-events-none" style={{
          backgroundColor: currentConfig.color
        }} data-id="element-529" />

          <svg className="w-full h-full transform -rotate-90 relative z-10" viewBox={`0 0 ${ringSize} ${ringSize}`} style={{
          filter: 'drop-shadow(0 0 12px var(--theme-color))'
        }} data-id="element-530">
            {/* Background Track */}
            <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke="#1f2937" strokeWidth={strokeWidth} data-id="element-531" />
            {/* Progress Ring */}
            <motion.circle initial={{
            strokeDashoffset: circumference
          }} animate={{
            strokeDashoffset
          }} transition={{
            duration: 1.5,
            ease: 'easeOut'
          }} cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke={currentConfig.color} strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={circumference} data-id="element-532" />
          </svg>

          {/* Center Text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-533">
            <span className="text-6xl font-black text-[#F0F0F0] tracking-tighter leading-none mb-1" data-id="element-534">
              {score}%
            </span>
            <span className="text-sm font-black font-mono tracking-widest uppercase" style={{
            color: currentConfig.color
          }} data-id="element-535">
              TOXIC
            </span>
          </div>
        </div>

        {/* Sub Header */}
        <div className="text-center mb-8" data-id="element-536">
          <h3 className="text-[#F0F0F0] font-mono text-xs tracking-widest uppercase bg-gray-900 px-4 py-2 rounded-full border border-gray-800 inline-block" data-id="element-537">
            {currentConfig.subHeader}
          </h3>
        </div>

        {/* Data Breakdown */}
        <div className="w-full bg-[#111] border border-gray-800 rounded-2xl p-5 mb-6" data-id="element-538">
          {renderDots(currentConfig.gaslighting, 'Gaslighting', level >= 3 ? 'Extreme' : level === 2 ? 'Moderate' : 'Low')}
          {renderDots(currentConfig.loveBombing, 'Love-Bombing', level >= 3 ? 'Maximum' : level === 2 ? 'Moderate' : 'Low')}
          {renderDots(currentConfig.darvo, 'DARVO Tactics', level >= 4 ? 'High' : level === 3 ? 'Moderate' : 'Low')}
        </div>

        {/* Validation Quote Box */}
        <div className="w-full border border-gray-800 bg-[#0A0A0A] p-5 relative rounded-xl" data-id="element-539">
          {/* Corner accents */}
          <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-gray-600 rounded-tl-xl" data-id="element-540" />
          <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-gray-600 rounded-tr-xl" data-id="element-541" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-gray-600 rounded-bl-xl" data-id="element-542" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-gray-600 rounded-br-xl" data-id="element-543" />

          <p className="text-[#F0F0F0] font-serif italic text-sm leading-relaxed text-center" data-id="element-544">
            "{currentConfig.quote}"
          </p>
        </div>

        {/* Vindication Element (Only shows for high scores) */}
        {score >= 70 && <div className="mt-4 text-center" data-id="element-545">
            <span className="text-[#00FF00] font-mono text-xs font-bold tracking-widest uppercase bg-[#00FF00]/10 px-3 py-1 rounded border border-[#00FF00]/30" data-id="element-546">
              Your intuition was 100% correct
            </span>
          </div>}
      </div>

      {/* Footer */}
      <footer className="p-5 border-t border-gray-800 bg-[#0A0A0A] relative z-10 text-center" data-id="element-547">
        <div className="font-black text-[#F0F0F0] text-lg tracking-tight mb-1" data-id="element-548">
          STOP DOUBTING. START KNOWING.
        </div>
        <div className="text-gray-500 font-mono text-xs tracking-widest mb-4" data-id="element-549">
          ToxicDetector.com
        </div>
        <div className="absolute bottom-2 right-3 text-[8px] font-mono text-gray-700 tracking-widest uppercase" data-id="element-550">
          ToxicDetector AI
        </div>
      </footer>
    </div>;
}